import { Action } from '@reduxjs/toolkit';
import { AxiosProgressEvent } from 'axios';
import { push } from 'connected-react-router';
import type { MemoryHistory } from 'history';
import { TFunction } from 'i18next';
import { last, values } from 'lodash/fp';
import { ThunkDispatch } from 'redux-thunk';
import ErrorCodes from 'shared/api/internal/verify/error_codes.json';

import { formatBytes } from 'dashboard/lib/utils/formatters';
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import {
  DocumentGovernmentIdFieldValue,
  GovIdPreparedFileUpload,
  SelfieFieldValue,
  SelfiePreparedFileUpload,
} from 'inquiry/lib/types';
import { setTransitionStart, setUiReady } from 'inquiry/redux/actions/ui';
import { pollInquiry, routeToNextStep } from 'inquiry/redux/thunk-actions/inquiry';
import { capturedViaUpload, getStepUri } from 'inquiry/redux/utils/governmentId';
import { ApiOptions } from 'lib/clients/ApiClient';
import EventsClient from 'lib/clients/EventsClient';
import {
  CapturedVideoData,
  logVideoCaptureSubmission,
  MediaRecorderManager,
} from 'lib/clients/mediaRecorder';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiNonRetryableResponseError from 'lib/errors/ApiNonRetryableResponseError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { blobToFile, createAttachment, createAttachments, fileTypeToExtension } from 'lib/file';
import { ApiEndpoint, ApiModel, ApiPayload, ApiResponse } from 'lib/interfaces/api';
import {
  StepGovernmentIdConfig,
  StepSelfieConfig,
  StepSelfieReflectionConfig,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import type { PageTransition } from 'lib/interfaces/ThemeSerialized';
import {
  GovernmentIdEnabledIdClassByCountryEntry,
  GovernmentIdSide,
  SelfieMatch,
} from 'lib/interfaces/verificationConfig';
import { captureException, captureMessage } from 'lib/reporting';
import { PreparedFileUpload } from 'lib/utils/dataUrl';
import { nullthrows } from 'lib/utils/nullthrows';
import { VerificationStatus } from 'lib/verification';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import SessionClientVerify from 'verify/lib/clients/SessionClientVerify';
import { clearMemoryHistory } from 'verify/lib/history';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { StoredFileUploads } from 'verify/lib/interfaces/fileUploads';
import FlowType from 'verify/lib/interfaces/FlowType';
import {
  CapturedSelfie,
  CapturedSelfiesSubmitData,
  SelfieStep,
} from 'verify/lib/interfaces/selfie';
import { getSelfiePose, selfiesCapturedViaUpload } from 'verify/lib/selfie';
import { BLANK_CAPTURE_BYTE_SIZE } from 'verify/lib/utils/webcam';
import { DisabledFileTypeError, MinDimensionSizeError } from 'verify/models/document';
import { getPollingMode, Inquiry, PollingMode } from 'verify/models/inquiry';
import { CaptureMethod, ClientExtraction } from 'verify/models/verification';
import { clearDocument } from 'verify/redux/actions/document';
import { logCollectedData, saveInquiry } from 'verify/redux/actions/inquiry';
import { showMessage } from 'verify/redux/actions/messages';
import { saveUploadProgress } from 'verify/redux/actions/verification';
import { UploadedFile } from 'verify/redux/reducers/document';
import { RootState } from 'verify/redux/store';
import { VerifyThunkAction } from 'verify/redux/types';
import { GovernmentIdStepAction } from 'verify/redux/types/verification';

interface SelfiePayloadAttributes {
  fields: Record<string, string | SelfieFieldValue>;
  'flow-type': FlowType;
}

interface CreateSelfiePayloadArgs {
  inquiry: Inquiry;
  config: StepSelfieConfig;
  capturedSelfiesSubmitData: CapturedSelfiesSubmitData;
  multipart: boolean;
}

async function createSelfiePayload({
  inquiry,
  config,
  capturedSelfiesSubmitData,
  multipart = true,
}: CreateSelfiePayloadArgs): Promise<ApiPayload<SelfiePayloadAttributes, ApiModel.Inquiry>> {
  const selfieType = config['selfie-type'];
  const { capturedVideoData } = capturedSelfiesSubmitData;
  let { capturedSelfies } = capturedSelfiesSubmitData;

  // SelfieScanner has some race conditions where it ends up saving multiple versions of the same pose.
  // Filter out repeats before submitting to the verification
  if (selfieType === SelfieMatch.ProfileAndCenter) {
    capturedSelfies = [
      getSelfiePose(capturedSelfies, SelfieStep.Center),
      getSelfiePose(capturedSelfies, SelfieStep.Left),
      getSelfiePose(capturedSelfies, SelfieStep.Right),
    ].filter(Boolean) as CapturedSelfie[];

    // TEMPORARY (PER-28421): For debugging why we occasionally get selfies / gov ids with missing files
    if (capturedSelfies.length < 3) {
      const capturedSelfiesOriginal = capturedSelfiesSubmitData.capturedSelfies;
      captureMessage(
        'Selfie ProfileAndCenter but client captured less than 3 poses',
        'warning',
        {
          capturedSelfiesMetadata: capturedSelfiesOriginal?.map((capturedSelfieOriginal) => {
            return {
              capturedAt: capturedSelfieOriginal?.capturedAt,
              captureMethod: capturedSelfieOriginal?.captureMethod,
              pose: capturedSelfieOriginal?.pose,
            };
          }),
          capturedVideoDataType: capturedVideoData?.type,
        },
        {
          jira: 'PER-28421',
        },
      );
    }
  }

  let files: SelfiePreparedFileUpload[] = capturedSelfies.map((selfie) => {
    const name =
      selfieType === SelfieMatch.OnlyCenter && selfie.pose === SelfieStep.Center
        ? 'photo'
        : `${selfie.pose}_photo`;
    return {
      name,
      type: 'image',
      'capture-method': selfie.captureMethod,
      'captured-at': selfie.capturedAt,
      frames: createAttachments(
        [
          {
            filename: `${name}.jpeg`,
            fileType: 'image/jpeg',
            dataUrl: selfie.dataUrl,
          },
        ],
        multipart,
      ),
    };
  });

  if (capturedVideoData?.type === 'stream') {
    files = files.concat([
      {
        name: 'video',
        type: 'video',
        'capture-method': CaptureMethod.Auto,
        frames: [],
        'object-id': capturedVideoData.objectId,
      },
    ]);
  } else if (capturedVideoData?.type === 'upload') {
    files = files.concat([
      {
        name: 'video',
        type: 'video',
        'capture-method': CaptureMethod.Auto,
        frames: [
          blobToFile(
            capturedVideoData.chunks,
            `video.${fileTypeToExtension(capturedVideoData.mimeType)}`,
            capturedVideoData.mimeType,
          ),
        ],
      },
    ]);
  }

  const selfie: SelfieFieldValue = {
    type: selfieType,
    files: files,
    'camera-properties': capturedSelfiesSubmitData.cameraInspectionData,
  };

  if (capturedSelfiesSubmitData.captureStartedAt) {
    selfie['capture-started-at'] = capturedSelfiesSubmitData.captureStartedAt;
  }

  return {
    id: inquiry.id,
    type: inquiry.type,
    attributes: {
      fields: {
        [config['field-key-selfie']]: selfie,
      },
      'flow-type': HolderClient.flowType,
    },
  };
}

export interface SelfieSubmitArgs {
  inquiry: Inquiry;
  config: StepSelfieConfig;
  capturedSelfiesSubmitData: CapturedSelfiesSubmitData;
  debugForcedStatus?: VerificationStatus;
  history: MemoryHistory;
  t: TFunction;
  featureFlags?: FeatureFlagsMap;
}

export function selfieSubmit({
  inquiry,
  config,
  capturedSelfiesSubmitData,
  debugForcedStatus,
  history,
  t,
}: SelfieSubmitArgs): VerifyThunkAction<Promise<void>> {
  return async (dispatch) => {
    let selfiePayload = await createSelfiePayload({
      inquiry,
      config,
      capturedSelfiesSubmitData,
      multipart: true,
    });
    let contentType: ApiOptions['contentType'] = 'multipart/form-data';

    if (!selfiesCapturedViaUpload(capturedSelfiesSubmitData.capturedSelfies)) {
      logVideoCaptureSubmission(
        config?.['enabled-capture-file-types'],
        capturedSelfiesSubmitData.capturedVideoData,
        'selfie',
      );
    }

    // Refresh waiting network status before starting attempt
    dispatch(saveUploadProgress(0));

    // Update includes a large image upload, so use retries
    const onUploadProgress = (event: AxiosProgressEvent) => {
      dispatch(saveUploadProgress(event.progress ?? 0));
    };
    const startTime = new Date().getTime();

    dispatch(setTransitionStart());

    let inquiryTransitionResponse: ApiResponse<Inquiry> | undefined = undefined;
    let attempt;
    for (attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        inquiryTransitionResponse = await SessionClientVerify.postResourceAction<
          Inquiry,
          ApiPayload<SelfiePayloadAttributes, ApiModel.Inquiry>
        >(selfiePayload, 'transition', {
          meta: {
            'from-component': inquiry.attributes['next-step'].name,
            'from-step': inquiry.attributes['next-step'].name,
            'workflow-initial-variables': {
              'debug-forced-status': debugForcedStatus,
            },
          },
          contentType,
          onUploadProgress,
        });

        // log data for client-side collection
        if (HolderClient.inquiryParams['send-client-side-collected-data-in-events']) {
          dispatch(
            logCollectedData({
              name: inquiry.attributes['next-step'].name,
              type: UiStepType.Selfie,
              data: capturedSelfiesSubmitData.capturedSelfies,
            }),
          );
        }
        break;
      } catch (err) {
        if (err instanceof ApiResponseError) {
          let retry = false;
          // Do not retry on the below error responses.
          switch (err.status) {
            case 502:
              // NOTE: 502 BAD GATEWAY results currently results from our backend's Puma configuration
              // having a timeout of 60 seconds. If an upload takes longer than 60 seconds, it times out,
              // nginx gets a status 408, and returns a status 502 to the client.
              // In this case, notify the user of connection trouble, so they can get better network
              // speed before retrying.
              dispatch(showMessage(t('errors.network-upload')));
              break;
            case 409: // Conflict -- inconsistent transition, etc.
              if (err.errors?.[0].code === 'inconsistent_transition_error') {
                // Client-server desync; resolve by refetching inquiry
                // Does not need session token provided; should be in SessionClientVerify already
                // Don't need to show an error message; should appear like transition succeeded to client
                const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                  ApiEndpoint.Inquiry,
                  inquiry.id,
                );
                dispatch(routeToNextStep(reloadedInquiry));
              } else if (err.errors?.[0].code === 'pending_transition_error') {
                // Transition is hanging for some reason. Treat as network error.
                dispatch(showMessage(t('errors.network')));
              } else {
                dispatch(showMessage(t('errors.inquiry-conflict')));
              }
              break;
            case 413: // Payload Too Large
              dispatch(showMessage(t('errors.image-upload-too-large')));
              break;
            case 422: // Unprocessable Entity
              /**
               * NOTE: iOS uploads can have issues here. This re-attempts once without multipart.
               * Sometimes we see a non-zero sized blob here, but see empty files in the controller.
               *
               * It MIGHT be caused by iCloud uploads.
               * See https://stackoverflow.com/questions/55011849/0-bytes-uploaded-of-file-stored-on-cloud-service-via-input-type-file-on-ios
               *
               * If this keeps happening, try forcing a file upload instead of using the native camera.
               */
              if (attempt === 0) {
                // letting proceed to default retry = true
                selfiePayload = await createSelfiePayload({
                  inquiry,
                  config,
                  capturedSelfiesSubmitData,
                  multipart: false,
                });
                contentType = 'application/json';
              } else {
                // TODO: Update with more appropriate error message
                dispatch(showMessage(t('government-id.prompt.failed-select-image')));
                break;
              }
            // eslint-disable-next-line no-fallthrough
            // Allow fallthrough for a specific branch in case 422
            default:
              retry = true;
          }

          if (retry === false) {
            dispatch(setUiReady());
            throw new ApiNonRetryableResponseError({ method: 'POST' });
          }
        } else if (!(err instanceof ApiNetworkError)) {
          captureException(err, 'error', { attempt });
        }

        if (attempt === MAX_RETRIES - 1) {
          dispatch(setUiReady());
          EventsClient.logAmplitude('selfie.submit.failed', {
            attempts: attempt,
            duration: new Date().getTime() - startTime,
          });
          throw err;
        }
      }
    }

    // Attempt to detect black images in submit
    try {
      ([SelfieStep.Center, SelfieStep.Left, SelfieStep.Right] as const).forEach((side) => {
        const upload = getSelfiePose(capturedSelfiesSubmitData.capturedSelfies, side);
        if (upload == null) return;
        const { dataUrl, captureMethod } = upload;

        if (dataUrl.length < BLANK_CAPTURE_BYTE_SIZE) {
          captureMessage(
            'Selfie verification contains potentially blank image',
            'warning',
            {},
            {
              captureMethod,
              side,
              videoEnabled: (capturedSelfiesSubmitData.capturedVideoData != null).toString(),
              videoCaptureMethod: capturedSelfiesSubmitData.capturedVideoData?.type ?? '',
            },
          );
        }
      });
    } catch (e) {
      captureException(e, 'warning');
    }

    if (inquiryTransitionResponse?.data == null) {
      const selfieType = config['selfie-type'];

      captureException(new Error('No data returned from create verification saga'), 'warning', {
        response: inquiryTransitionResponse,
        selfieType,
        captureMethod: getSelfiePose(capturedSelfiesSubmitData.capturedSelfies, SelfieStep.Center)
          ?.captureMethod,
        uploads: {
          centerSize: getSelfiePose(capturedSelfiesSubmitData.capturedSelfies, SelfieStep.Center)
            ?.dataUrl.length,
          leftSize: getSelfiePose(capturedSelfiesSubmitData.capturedSelfies, SelfieStep.Left)
            ?.dataUrl.length,
          rightSize: getSelfiePose(capturedSelfiesSubmitData.capturedSelfies, SelfieStep.Right)
            ?.dataUrl.length,
        },
      });

      // TODO: Update with more appropriate error message
      // This error is more specific and is somewhat accurate as far as we know
      // + it prompts the user to retry
      dispatch(showMessage(t('government-id.prompt.failed-select-image')));
      dispatch(setUiReady());
      throw new ApiNonRetryableResponseError({ method: 'POST' });
    }

    EventsClient.logAmplitude('selfie.submit.completed', {
      duration: new Date().getTime() - startTime,
      attempts: attempt + 1,
    });

    const { data: nextInquiry } = inquiryTransitionResponse;
    dispatch(saveInquiry(nextInquiry));
    dispatch(saveUploadProgress(0));
    if (capturedSelfiesSubmitData.capturedVideoData) {
      MediaRecorderManager.markClientSuccess();
    }

    // Only poll if waiting for workflow run
    const pollingMode = getPollingMode(nextInquiry);

    if (pollingMode === PollingMode.BLOCKING) {
      await dispatch(pollInquiry(nextInquiry, { historyToClear: history }));
    } else {
      dispatch(setUiReady());
      dispatch(routeToNextStep(nextInquiry));
    }
  };
}

interface GovIdPayloadAttributes {
  fields: Record<string, string | DocumentGovernmentIdFieldValue | undefined>;
  'flow-type': FlowType;
}

interface CreateGovIdPayloadArgs {
  inquiry: Inquiry;
  config: StepGovernmentIdConfig;
  governmentIdClass: GovernmentIdClass;
  countryCode: CountryCode | undefined;
  uploads: StoredFileUploads<GovernmentIdSide>;
  clientExtractionRaws: ClientExtraction[];
  clientExtractionSource: string;
  cameraInspectionData: CameraInspectionData | undefined;
  videoData: CapturedVideoData | undefined;
  multipart: boolean;
}

function createGovIdPayload({
  inquiry,
  config,
  governmentIdClass,
  countryCode,
  uploads,
  clientExtractionRaws,
  clientExtractionSource,
  cameraInspectionData,
  videoData,
  multipart = true,
}: CreateGovIdPayloadArgs): ApiPayload<GovIdPayloadAttributes, ApiModel.Inquiry> {
  const frontFrames = uploads[GovernmentIdSide.Front] || uploads[GovernmentIdSide.FrontOrBack];
  const backFrames = uploads[GovernmentIdSide.Back] || uploads[GovernmentIdSide.BarcodePdf417];
  const signatureFrames = uploads[GovernmentIdSide.PassportSignature];

  const files: GovIdPreparedFileUpload[] = [
    {
      page: GovernmentIdSide.Front,
      'capture-method': last(frontFrames)?.captureMethod,
      'capture-flow': last(frontFrames)?.captureFlow,
      frames: createAttachments(frontFrames, multipart),
    },
    {
      page: GovernmentIdSide.Back,
      'capture-method': last(backFrames)?.captureMethod,
      'capture-flow': last(backFrames)?.captureFlow,
      frames: createAttachments(backFrames, multipart),
    },
    {
      page: GovernmentIdSide.PassportSignature,
      'capture-method': last(signatureFrames)?.captureMethod,
      'capture-flow': last(signatureFrames)?.captureFlow,
      frames: createAttachments(signatureFrames, multipart),
    },
  ];

  if (videoData) {
    if (videoData.type === 'stream') {
      files.push({
        page: 'front_and_back',
        type: 'video',
        'capture-flow': undefined,
        'capture-method': undefined,
        frames: [],
        'object-id': videoData.objectId,
      });
    } else if (videoData.type === 'upload') {
      files.push({
        page: 'front_and_back',
        type: 'video',
        'capture-flow': undefined,
        'capture-method': undefined,
        frames: [
          blobToFile(
            videoData.chunks,
            `video.${fileTypeToExtension(videoData.mimeType)}`,
            videoData.mimeType,
          ),
        ],
      });
    }
  }

  const document = {
    'client-extraction-source': clientExtractionSource,
    'client-extraction-raws': clientExtractionRaws,
    'camera-properties': cameraInspectionData,
    files: files.filter((f) => f['frames'].length !== 0 || f['object-id'] != null),
  };

  const fields: Record<string, string | DocumentGovernmentIdFieldValue | undefined> = {
    [config['field-key-idclass']]: governmentIdClass,
    [config['field-key-document']]: document,
  };
  if (countryCode != null && config['field-key-country'] != null) {
    fields[config['field-key-country']] = countryCode.toUpperCase();
  }

  return {
    id: inquiry.id,
    type: inquiry.type,
    attributes: {
      fields: fields,
      'flow-type': HolderClient.flowType,
    },
  };
}

const MAX_RETRIES = 3;

export interface SubmitGovernmentIdArgs {
  inquiry: Inquiry;
  config: StepGovernmentIdConfig;
  governmentIdClass: GovernmentIdClass;
  countryCode: CountryCode | undefined;
  uploads: StoredFileUploads<GovernmentIdSide>;
  clientExtractionRaws: ClientExtraction[];
  clientExtractionSource: string;
  manuallyClassifiedInAutoclassificationFlow: boolean;
  cameraInspectionData?: CameraInspectionData;
  videoData?: CapturedVideoData;
  debugForcedStatus?: VerificationStatus;
  history: MemoryHistory;
  t: TFunction;
  opts?: { transition?: PageTransition };
}

export function submitGovernmentId({
  inquiry,
  config,
  governmentIdClass,
  countryCode,
  uploads,
  clientExtractionRaws,
  clientExtractionSource,
  cameraInspectionData,
  videoData,
  manuallyClassifiedInAutoclassificationFlow,
  debugForcedStatus,
  history,
  t,
  opts = {},
}: SubmitGovernmentIdArgs): VerifyThunkAction {
  return async (dispatch) => {
    let govIdPayload = createGovIdPayload({
      inquiry,
      config,
      governmentIdClass,
      countryCode,
      uploads,
      clientExtractionRaws,
      clientExtractionSource,
      cameraInspectionData,
      videoData,
      multipart: true,
    });
    let contentType: ApiOptions['contentType'] = 'multipart/form-data';

    if (!capturedViaUpload(uploads)) {
      logVideoCaptureSubmission(config?.['enabled-capture-file-types'], videoData, 'government-id');
    }

    // Refresh waiting network status before starting attempt
    dispatch(saveUploadProgress(0));

    // Update includes a large image upload, so use retries
    const onUploadProgress = (event: AxiosProgressEvent) => {
      dispatch(saveUploadProgress(event.progress ?? 0));
    };
    const startTime = new Date().getTime();

    dispatch(setTransitionStart());

    let inquiryTransitionResponse: ApiResponse<Inquiry> | undefined = undefined;
    let attempt;
    for (attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        inquiryTransitionResponse = await SessionClientVerify.postResourceAction<
          Inquiry,
          ApiPayload<GovIdPayloadAttributes, ApiModel.Inquiry>
        >(govIdPayload, 'transition', {
          meta: {
            'from-component': inquiry.attributes['next-step'].name,
            'from-step': inquiry.attributes['next-step'].name,
            'manually-classified-in-autoclassification-flow':
              manuallyClassifiedInAutoclassificationFlow,
            'workflow-initial-variables': {
              'debug-forced-status': debugForcedStatus,
            },
          },
          contentType,
          onUploadProgress,
        });

        // log data for client-side collection
        if (HolderClient.inquiryParams['send-client-side-collected-data-in-events']) {
          dispatch(
            logCollectedData({
              name: inquiry.attributes['next-step'].name,
              type: UiStepType.GovernmentId,
              data: uploads,
            }),
          );
        }
        break;
      } catch (err) {
        if (err instanceof ApiResponseError) {
          let retry = false;
          // Do not retry on the below error responses.
          switch (err.status) {
            case 502:
              // NOTE: 502 BAD GATEWAY results currently results from our backend's Puma configuration
              // having a timeout of 60 seconds. If an upload takes longer than 60 seconds, it times out,
              // nginx gets a status 408, and returns a status 502 to the client.
              // In this case, notify the user of connection trouble, so they can get better network
              // speed before retrying.
              dispatch(showMessage(t('errors.network-upload')));
              break;
            case 401:
              // SessionClientVerify should redirect to start on 401
              // Return here to avoid dereferencing a null inquiryTransitionResponse
              return;
            case 409: // Conflict -- inconsistent transition, etc.
              switch (err.errors?.[0].code) {
                case 'inconsistent_transition_error': {
                  // Client-server desync; resolve by refetching inquiry
                  // Does not need session token provided; should be in SessionClientVerify already
                  // Don't need to show an error message; should appear like transition succeeded to client
                  const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                    ApiEndpoint.Inquiry,
                    inquiry.id,
                  );
                  dispatch(routeToNextStep(reloadedInquiry));
                  break;
                }
                case 'pending_transition_error': {
                  // Transition is hanging for some reason. Treat as network error.
                  dispatch(showMessage(t('errors.network')));
                  break;
                }
                default: {
                  dispatch(showMessage(t('errors.inquiry-conflict')));
                }
              }
              break;
            case 413: // Payload Too Large
              dispatch(showMessage(t('errors.image-upload-too-large')));
              break;
            case 422: // Unprocessable Entity
              /**
               * NOTE: iOS uploads can have issues here. This re-attempts once without multipart.
               * Sometimes we see a non-zero sized blob here, but see empty files in the controller.
               *
               * It MIGHT be caused by iCloud uploads.
               * See https://stackoverflow.com/questions/55011849/0-bytes-uploaded-of-file-stored-on-cloud-service-via-input-type-file-on-ios
               *
               * If this keeps happening, try forcing a file upload instead of using the native camera.
               */
              if (attempt === 0) {
                govIdPayload = createGovIdPayload({
                  inquiry,
                  config,
                  governmentIdClass,
                  countryCode,
                  uploads,
                  clientExtractionRaws,
                  clientExtractionSource,
                  cameraInspectionData,
                  videoData,
                  multipart: false,
                });
                contentType = 'application/json';
                retry = true;
              } else {
                handleGovernmentIdUnprocessableEntityErrors(err, history, dispatch, t);
              }
              break;
            default:
              retry = true;
          }

          if (retry === false) {
            dispatch(setUiReady());
            throw new ApiNonRetryableResponseError({ method: 'POST' });
          }
        } else if (!(err instanceof ApiNetworkError)) {
          captureException(err, 'error', { attempt });
        }

        if (attempt === MAX_RETRIES - 1) {
          dispatch(setUiReady());
          EventsClient.logAmplitude('government-id.submit.failed', {
            attempts: attempt,
            duration: new Date().getTime() - startTime,
          });
          throw err;
        }
      }
    }

    // Attempt to detect black images in submit
    try {
      [
        GovernmentIdSide.Front,
        GovernmentIdSide.Back,
        GovernmentIdSide.BarcodePdf417,
        GovernmentIdSide.FrontOrBack,
        GovernmentIdSide.PassportSignature,
      ].forEach((side) => {
        uploads[side]?.forEach((upload) => {
          if (upload == null) return;
          const { dataUrl, fileType, captureMethod, captureFlow } = upload;

          if (dataUrl.length < BLANK_CAPTURE_BYTE_SIZE) {
            captureMessage(
              'Government ID verification contains potentially blank image',
              'warning',
              {},
              {
                captureMethod,
                side,
                fileType,
                captureFlow,
                videoEnabled: (videoData != null).toString(),
                videoCaptureMethod: videoData?.type ?? '',
              },
            );
          }
        });
      });
    } catch (e) {
      captureException(e, 'warning');
    }

    if (inquiryTransitionResponse?.data == null) {
      captureException(new Error('No data returned from create verification saga'), 'warning', {
        response: inquiryTransitionResponse,
        governmentIdClass,
        captureMethod: values(uploads).map((files) => last(files)?.captureMethod),
        captureFlow: values(uploads).map((files) => last(files)?.captureFlow),
        uploads: {
          backSizes: uploads.back?.map((file) => file.dataUrl?.length),
          frontSizes: uploads.front?.map((file) => file.dataUrl?.length),
        },
      });
      // This error is more specific and is somewhat accurate as far as we know
      // + it prompts the user to retry
      dispatch(showMessage(t('government-id.prompt.failed-select-image')));
      dispatch(setUiReady());
      throw new ApiNonRetryableResponseError({ method: 'POST' });
    }

    EventsClient.logAmplitude('government-id.submit.completed', {
      duration: new Date().getTime() - startTime,
      attempts: attempt + 1,
    });

    const { data: nextInquiry } = inquiryTransitionResponse;
    dispatch(saveInquiry(nextInquiry));
    dispatch(saveUploadProgress(0));

    if (videoData) {
      MediaRecorderManager.markClientSuccess();
    }

    // Only poll if waiting for workflow run
    const pollingMode = getPollingMode(nextInquiry);

    if (pollingMode === PollingMode.BLOCKING) {
      dispatch(push('/inquiry/government-id/pending', { transition: opts.transition }));
    } else {
      dispatch(setUiReady());
      dispatch(routeToNextStep(nextInquiry));
    }
  };
}

interface GovIdAutoclassificationPayload {
  image: PreparedFileUpload;
  'client-extracted-text'?: string;
}

export type AutoclassificationFailureReason =
  | AutoclassificationResponseType.REJECTED
  | AutoclassificationResponseType.FAILED;

export enum AutoclassificationResponseType {
  ACCEPTED = 'id_accepted',
  REJECTED = 'id_rejected',
  FAILED = 'classification_failed',
}

export interface IdClassesByCountry {
  'country-code': CountryCode;
  'country-name': string;
  'id-configs': GovernmentIdEnabledIdClassByCountryEntry[];
}

export interface AutoclassificationResponse {
  /**
   * The result of autoclassification - describes whether id was accepted, rejected, or autoclassification failed
   */
  'response-type': AutoclassificationResponseType;
  /**
   * This is only populated when response-type is id_rejected or classification_failed. This represents the list of
   * enabled id classes by country for the current template.
   */
  'id-classes-by-countries'?: IdClassesByCountry[];
  /**
   * This is only populated when response-type is id_accepted. This is the country code of the detected ID.
   */
  'country-code'?: CountryCode;
  /**
   * This is only populated when response-type is id_accepted. This is the id class of the detected ID.
   */
  'id-class'?: GovernmentIdClass;
  /**
   * This is only populated when response-type is id_accepted. This is the config (e.g. capture configs) for the detected ID.
   */
  'id-config'?: GovernmentIdEnabledIdClassByCountryEntry;
}

export interface GovernmentIdAutoclassificationArgs {
  inquiry: Inquiry;
  uploads: StoredFileUploads<GovernmentIdSide>;
  history: MemoryHistory;
  t: TFunction;
  debugForcedStatus?: VerificationStatus;
}

export function governmentIdAutoclassification({
  inquiry,
  uploads,
  history,
  t,
  debugForcedStatus,
}: GovernmentIdAutoclassificationArgs): VerifyThunkAction<Promise<AutoclassificationResponse>> {
  return async (dispatch) => {
    const frontFrames = uploads[GovernmentIdSide.Front];
    const image = createAttachment(nullthrows(last(frontFrames)), true);

    let autoclassificationResponseTemp: AutoclassificationResponse | null = null;
    let attempt;
    for (attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        autoclassificationResponseTemp = (await SessionClientVerify.postResourceAction<
          Inquiry,
          ApiPayload<GovIdAutoclassificationPayload, ApiModel.Inquiry>
        >(
          {
            attributes: {
              image: image,
            },
            id: inquiry.id,
            type: inquiry.type,
          },
          'government-id-classification',
          {
            meta: {
              'workflow-initial-variables': {
                'debug-forced-status': debugForcedStatus,
              },
            },
            contentType: 'multipart/form-data',
          },
        )) as unknown as AutoclassificationResponse;
        break;
      } catch (err) {
        if (err instanceof ApiResponseError) {
          let retry = false;
          // Do not retry on the below error responses.
          switch (err.status) {
            case 502:
              // NOTE: 502 BAD GATEWAY results currently results from our backend's Puma configuration
              // having a timeout of 60 seconds. If an upload takes longer than 60 seconds, it times out,
              // nginx gets a status 408, and returns a status 502 to the client.
              // In this case, notify the user of connection trouble, so they can get better network
              // speed before retrying.
              dispatch(showMessage(t('errors.network-upload')));
              break;
            case 409: // Conflict -- inconsistent transition, etc.
              switch (err.errors?.[0].code) {
                case 'inconsistent_transition_error': {
                  // Client-server desync; resolve by refetching inquiry
                  // Does not need session token provided; should be in SessionClientVerify already
                  // Don't need to show an error message; should appear like transition succeeded to client
                  const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                    ApiEndpoint.Inquiry,
                    inquiry.id,
                  );
                  dispatch(routeToNextStep(reloadedInquiry));
                  break;
                }
                default: {
                  dispatch(showMessage(t('errors.inquiry-conflict')));
                }
              }
              break;
            case 413: // Payload Too Large
              dispatch(showMessage(t('errors.image-upload-too-large')));
              break;
            case 422: // Unprocessable Entity
              handleGovernmentIdUnprocessableEntityErrors(err, history, dispatch, t);
              break;
            default:
              retry = true;
          }

          if (retry === false) {
            dispatch(setUiReady());
            throw new ApiNonRetryableResponseError({ method: 'POST' });
          }
        } else if (!(err instanceof ApiNetworkError)) {
          captureException(err, 'error', { attempt });
        }

        if (attempt === MAX_RETRIES - 1) {
          dispatch(setUiReady());
          throw err;
        }
      }
    }

    try {
      // uppercase country code since we do comparisons assuming upper case - notably for isMicroblinkSupportedSide
      if (autoclassificationResponseTemp?.['country-code']) {
        autoclassificationResponseTemp['country-code'] = autoclassificationResponseTemp[
          'country-code'
        ].toUpperCase() as CountryCode;
      }
      autoclassificationResponseTemp?.['id-classes-by-countries']?.forEach(
        (idClassesForCountry) => {
          idClassesForCountry['country-code'] = idClassesForCountry[
            'country-code'
          ].toUpperCase() as CountryCode;
        },
      );
      return nullthrows(autoclassificationResponseTemp);
    } catch (err) {
      captureException(err, 'warning');
      throw new ApiNonRetryableResponseError({ method: 'POST' });
    }
  };
}

// handles the different possible error codes from gov id validation and shows
// the appropriate error message for each, as well as any routing required
function handleGovernmentIdUnprocessableEntityErrors(
  err: ApiResponseError,
  history: MemoryHistory,
  dispatch: ThunkDispatch<RootState, void, Action>,
  t: TFunction,
) {
  const errorCode = err.errors?.[0].code;
  switch (errorCode) {
    case ErrorCodes.DOCUMENT_GOVERNMENT_ID_DIMENSION_SIZE_ERROR: {
      const error = err.errors?.[0] as MinDimensionSizeError;
      const minDimensionSize = error.details?.min_dimension_size;

      clearMemoryHistory(history);
      history.push(getStepUri(0, GovernmentIdStepAction.Prompt));
      dispatch(
        showMessage(
          t('government-id.prompt.dimension-size-too-small', {
            minDimensionSize,
          }),
        ),
      );
      dispatch(setUiReady());
      return; // don't continue submitting at all
    }
    case ErrorCodes.DOCUMENT_GOVERNMENT_ID_TOO_SMALL:
    case ErrorCodes.DOCUMENT_GOVERNMENT_ID_ALL_BLACK_IMAGE: {
      // These generally happen when a device OOM results in an all-black image being captured.
      // Black screen captures are transient, so go back to first capture page to force restart of BaseScanner.
      clearMemoryHistory(history);
      history.push(getStepUri(0, GovernmentIdStepAction.Prompt));
      dispatch(showMessage(t('government-id.prompt.failed-black-image')));
      dispatch(setUiReady());
      return; // don't continue submitting at all
    }
    case ErrorCodes.DOCUMENT_GOVERNMENT_ID_DISABLED_FILE_TYPE_ERROR: {
      const error = err.errors?.[0] as DisabledFileTypeError;
      const errorDetails = error.details;
      const uploadedFileExtension = fileTypeToExtension(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        errorDetails.uploaded_file_type,
      ).toUpperCase();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const acceptedFileExtensions = errorDetails.enabled_file_types.map((t) =>
        fileTypeToExtension(t).toUpperCase(),
      );

      clearMemoryHistory(history);
      history.push(getStepUri(0, GovernmentIdStepAction.Prompt));
      dispatch(
        showMessage(
          t('documents.errors.disabled-file-type', {
            uploadedFileExtension: uploadedFileExtension,
            acceptedFileExtensions: acceptedFileExtensions.join(', '),
            count: acceptedFileExtensions.length,
          }),
        ),
      );
      dispatch(setUiReady());
      return; // don't continue submitting at all
    }
    default: {
      if (errorCode && /must be under \d+ bytes/.test(errorCode)) {
        const match = errorCode.match(/must be under (\d+) bytes/);
        if (match) {
          const fileSizeLimit = formatBytes(parseInt(match[1]));
          dispatch(
            showMessage(
              t('documents.errors.file-size-exceeded', {
                fileSizeLimit,
              }),
            ),
          );
          return;
        }
      }
      dispatch(showMessage(t('government-id.prompt.failed-select-image')));
      break;
    }
  }
}

export interface DocumentSubmitArgs {
  inquiry: Inquiry;
  debugForcedStatus?: VerificationStatus;
  t: TFunction;
  uploadedFiles: UploadedFile[];
}

export function documentSubmit({
  inquiry,
  debugForcedStatus,
  t,
  uploadedFiles,
}: DocumentSubmitArgs): VerifyThunkAction<Promise<void>> {
  return async (dispatch) => {
    // Refresh waiting network status before starting attempt
    dispatch(saveUploadProgress(0));

    // Update includes a large image upload, so use retries
    const onUploadProgress = (event: AxiosProgressEvent) => {
      dispatch(saveUploadProgress(event.progress ?? 0));
    };
    const startTime = new Date().getTime();

    dispatch(setTransitionStart());

    let inquiryTransitionResponse: ApiResponse<Inquiry> | undefined = undefined;
    let attempt;
    for (attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        inquiryTransitionResponse = await SessionClientVerify.postResourceAction(
          {
            type: inquiry.type,
            id: inquiry.id,
            attributes: {
              'flow-type': HolderClient.flowType,
            },
          },
          'transition',
          {
            meta: {
              'from-component': inquiry.attributes['next-step'].name,
              'from-step': inquiry.attributes['next-step'].name,
              'workflow-initial-variables': {
                'debug-forced-status': debugForcedStatus,
              },
            },
            onUploadProgress,
          },
        );

        // log data for client-side collection
        if (HolderClient.inquiryParams['send-client-side-collected-data-in-events']) {
          dispatch(
            logCollectedData({
              name: inquiry.attributes['next-step'].name,
              type: UiStepType.Document,
              data: (uploadedFiles || []).map((uploadedFile) => uploadedFile.file),
            }),
          );
        }
        break;
      } catch (err) {
        if (err instanceof ApiResponseError) {
          let retry = false;
          // Do not retry on the below error responses.
          switch (err.status) {
            case 502:
              // NOTE: 502 BAD GATEWAY results currently results from our backend's Puma configuration
              // having a timeout of 60 seconds. If an upload takes longer than 60 seconds, it times out,
              // nginx gets a status 408, and returns a status 502 to the client.
              // In this case, notify the user of connection trouble, so they can get better network
              // speed before retrying.
              dispatch(showMessage(t('errors.network-upload')));
              break;
            case 409: // Conflict -- inconsistent transition, etc.
              if (err.errors?.[0].code === 'inconsistent_transition_error') {
                // Client-server desync; resolve by refetching inquiry
                // Does not need session token provided; should be in SessionClientVerify already
                // Don't need to show an error message; should appear like transition succeeded to client
                const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                  ApiEndpoint.Inquiry,
                  inquiry.id,
                );
                dispatch(routeToNextStep(reloadedInquiry));
              } else if (err.errors?.[0].code === 'pending_transition_error') {
                // Transition is hanging for some reason. Treat as network error.
                dispatch(showMessage(t('errors.network')));
              } else {
                dispatch(showMessage(t('errors.inquiry-conflict')));
              }
              break;
            case 413: // Payload Too Large
              dispatch(showMessage(t('errors.image-upload-too-large')));
              break;
            case 422: // Unprocessable Entity
              // TODO: Update with more appropriate error message
              dispatch(showMessage(t('government-id.prompt.failed-select-image')));
              break;
            default:
              retry = true;
          }

          if (retry === false) {
            dispatch(setUiReady());
            throw new ApiNonRetryableResponseError({ method: 'POST' });
          }
        } else if (!(err instanceof ApiNetworkError)) {
          captureException(err, 'error', { attempt });
        }

        if (attempt === MAX_RETRIES - 1) {
          dispatch(setUiReady());
          EventsClient.logAmplitude('document.submit.failed', {
            attempts: attempt,
            duration: new Date().getTime() - startTime,
          });
          throw err;
        }
      }
    }

    if (inquiryTransitionResponse?.data == null) {
      captureException(
        new Error('No data returned from create document verification saga'),
        'warning',
        {
          response: inquiryTransitionResponse,
        },
      );

      // TODO: Update with more appropriate error message
      // This error is more specific and is somewhat accurate as far as we know
      // + it prompts the user to retry
      dispatch(showMessage(t('government-id.prompt.failed-select-image')));
      dispatch(setUiReady());
      throw new ApiNonRetryableResponseError({ method: 'POST' });
    }

    EventsClient.logAmplitude('document.submit.completed', {
      duration: new Date().getTime() - startTime,
      attempts: attempt + 1,
    });

    const { data: nextInquiry } = inquiryTransitionResponse;
    dispatch(saveInquiry(nextInquiry));
    dispatch(clearDocument());

    // Only poll if waiting for workflow run
    const pollingMode = getPollingMode(nextInquiry);

    if (pollingMode === PollingMode.BLOCKING) {
      dispatch(push('/inquiry/documents/pending'));
    } else {
      dispatch(setUiReady());
      dispatch(routeToNextStep(nextInquiry));
    }
  };
}

export interface SelfieReflectionSubmitArgs {
  inquiry: Inquiry;
  config: StepSelfieReflectionConfig;
  sessionId: string;
  debugForcedStatus?: VerificationStatus;
  history: MemoryHistory;
  t: TFunction;
}

export function selfieReflectionSubmit({
  inquiry,
  config,
  sessionId,
  debugForcedStatus,
  history,
  t,
}: SelfieReflectionSubmitArgs): VerifyThunkAction<Promise<void>> {
  return async (dispatch) => {
    const selfiePayload: ApiPayload<SelfiePayloadAttributes, ApiModel.Inquiry> = {
      id: inquiry.id,
      type: inquiry.type,
      attributes: {
        fields: {
          [config['field-key-selfie']]: {
            type: 'reflection',
            'reflection-session-id': sessionId,
            files: [],
          },
        },
        'flow-type': HolderClient.flowType,
      },
    };

    const startTime = new Date().getTime();

    dispatch(setTransitionStart());
    const contentType: ApiOptions['contentType'] = 'multipart/form-data';
    let inquiryTransitionResponse: ApiResponse<Inquiry> | undefined = undefined;
    let attempt;
    for (attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        inquiryTransitionResponse = await SessionClientVerify.postResourceAction<
          Inquiry,
          ApiPayload<SelfiePayloadAttributes, ApiModel.Inquiry>
        >(selfiePayload, 'transition', {
          meta: {
            'from-component': inquiry.attributes['next-step'].name,
            'from-step': inquiry.attributes['next-step'].name,
            'workflow-initial-variables': {
              'debug-forced-status': debugForcedStatus,
            },
          },
          contentType,
        });
        break;
      } catch (err) {
        if (err instanceof ApiResponseError) {
          let retry = false;
          // Do not retry on the below error responses.
          switch (err.status) {
            case 502:
              // NOTE: 502 BAD GATEWAY results currently results from our backend's Puma configuration
              // having a timeout of 60 seconds. If an upload takes longer than 60 seconds, it times out,
              // nginx gets a status 408, and returns a status 502 to the client.
              // In this case, notify the user of connection trouble, so they can get better network
              // speed before retrying.
              dispatch(showMessage(t('errors.network-upload')));
              break;
            case 409: // Conflict -- inconsistent transition, etc.
              if (err.errors?.[0].code === 'inconsistent_transition_error') {
                // Client-server desync; resolve by refetching inquiry
                // Does not need session token provided; should be in SessionClientVerify already
                // Don't need to show an error message; should appear like transition succeeded to client
                const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                  ApiEndpoint.Inquiry,
                  inquiry.id,
                );
                dispatch(routeToNextStep(reloadedInquiry));
              } else if (err.errors?.[0].code === 'pending_transition_error') {
                // Transition is hanging for some reason. Treat as network error.
                dispatch(showMessage(t('errors.network')));
              } else {
                dispatch(showMessage(t('errors.inquiry-conflict')));
              }
              break;
            case 413: // Payload Too Large
              dispatch(showMessage(t('errors.image-upload-too-large')));
              break;
            case 422: // Unprocessable Entity
              // TODO: Update with more appropriate error message
              dispatch(showMessage(t('government-id.prompt.failed-select-image')));
              break;
            // eslint-disable-next-line no-fallthrough
            // Allow fallthrough for a specific branch in case 422
            default:
              retry = true;
          }

          if (retry === false) {
            dispatch(setUiReady());
            throw new ApiNonRetryableResponseError({ method: 'POST' });
          }
        } else if (!(err instanceof ApiNetworkError)) {
          captureException(err, 'error', { attempt });
        }

        if (attempt === MAX_RETRIES - 1) {
          dispatch(setUiReady());
          EventsClient.logAmplitude('selfie-reflection.submit.failed', {
            attempts: attempt,
            duration: new Date().getTime() - startTime,
          });
          throw err;
        }
      }
    }

    if (inquiryTransitionResponse?.data == null) {
      const selfieType = config['selfie-type'];

      captureException(new Error('No data returned from create verification saga'), 'warning', {
        response: inquiryTransitionResponse,
        selfieType,
      });

      // TODO: Update with more appropriate error message
      // This error is more specific and is somewhat accurate as far as we know
      // + it prompts the user to retry
      dispatch(showMessage(t('government-id.prompt.failed-select-image')));
      dispatch(setUiReady());
      throw new ApiNonRetryableResponseError({ method: 'POST' });
    }

    EventsClient.logAmplitude('selfie-reflection.submit.completed', {
      duration: new Date().getTime() - startTime,
      attempts: attempt + 1,
    });

    const { data: nextInquiry } = inquiryTransitionResponse;
    dispatch(saveInquiry(nextInquiry));
    dispatch(saveUploadProgress(0));

    // Only poll if waiting for workflow run
    const pollingMode = getPollingMode(nextInquiry);

    if (pollingMode === PollingMode.BLOCKING) {
      await dispatch(pollInquiry(nextInquiry, { historyToClear: history }));
    } else {
      dispatch(setUiReady());
      dispatch(routeToNextStep(nextInquiry));
    }
  };
}
